import {
  GetParentGuardianInvitationDetailsResponse,
  GetParentGuardianInvitationResponse,
  GetRelationshipsResponse,
  GetYouthAdventuresRequest,
  GetYouthAdventuresResponse,
  GetYouthAwardsRequest,
  GetYouthAwardsResponse,
  GetYouthMeritBadgesRequest,
  GetYouthMeritBadgesResponse,
  GetYouthRanksAdvancementRequest,
  GetYouthRanksAdvancementResponse,
  GetYouthSSElectivesResponse,
  PostParentGuardianInvitationAcceptRequest,
  PostParentGuardianInvitationRequest,
  PostRelationshipRequest,
  PostRelationshipSearchRequest,
  PostRelationshipSearchResponse,
  PostUserMeritBadgeBlueCardsReq,
  PostUserMeritBadgeBlueCardsRes,
  PutParentGuardianInvitationRequest,
} from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';

export const youthProfileApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/adventures
     */
    getYouthAdventures: builder.query<
      GetYouthAdventuresResponse,
      {
        userId: string;
        params: GetYouthAdventuresRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/adventures`,
        method: 'GET',
        params: arg.params,
      }),
      providesTags: (_result, error, arg) =>
        error
          ? []
          : [
              {
                type: tagTypes.YouthAdventure,
                id: arg.userId,
              },
            ],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/awards
     */
    getYouthAwards: builder.query<
      GetYouthAwardsResponse,
      {
        userId: string;
        params: GetYouthAwardsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/awards`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthAward,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/meritBadges
     */
    getYouthMeritBadges: builder.query<
      GetYouthMeritBadgesResponse,
      {
        userId: string;
        params: GetYouthMeritBadgesRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/meritBadges`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthMeritBadge,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ranks
     */
    getYouthRanks: builder.query<
      GetYouthRanksAdvancementResponse,
      {
        userId: string;
        params: GetYouthRanksAdvancementRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/ranks`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthRanks,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint GET /advancements/v2/youth/:userId/ssElectives
     */
    getYouthSSElectives: builder.query<
      GetYouthSSElectivesResponse,
      {
        userId: string;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/youth/${arg.userId}/ssElectives`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) =>
        result && !error
          ? [
              {
                type: tagTypes.YouthSSElectives,
                id: arg.userId,
              },
            ]
          : [],
    }),
    /**
     * @esbEndpoint POST /advancements/v2/organization/:organizationGuid/meritBadges/printBlueCard
     */
    postMeritBadgeBlueCards: builder.query<
      PostUserMeritBadgeBlueCardsRes,
      {
        organizationGuid: string;
        payload: PostUserMeritBadgeBlueCardsReq;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/organization/${arg.organizationGuid}/meritBadges/printBlueCard`,
        method: 'POST',
        body: arg.payload,
        responseHandler: response => response.blob(),
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/relationshipSearch
     */
    postRelationshipSearch: builder.mutation<
      PostRelationshipSearchResponse,
      {
        payload: PostRelationshipSearchRequest;
      }
    >({
      query: arg => ({
        url: '/persons/v2/relationshipSearch',
        method: 'POST',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/relationships
     */
    getRelationships: builder.query<
      GetRelationshipsResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships`,
        method: 'GET',
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/relationships
     */
    postRelationship: builder.mutation<
      void,
      {
        payload: PostRelationshipRequest;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships`,
        method: 'POST',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint PUT /persons/v2/:personGuid/relationships/:relationshipId
     */
    putRelationship: builder.mutation<
      void,
      {
        payload: PostRelationshipRequest;
        relationshipId: string;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships/${arg.relationshipId}`,
        method: 'PUT',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint DELETE /persons/v2/:personGuid/relationships/:relationshipId
     */
    deleteRelationship: builder.mutation<
      void,
      {
        relationshipId: string;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/relationships/${arg.relationshipId}`,
        method: 'DELETE',
      }),
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/parentGuardianInvitation
     */
    getParentGuardianInvitation: builder.query<
      GetParentGuardianInvitationResponse,
      {
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation`,
        method: 'GET',
      }),
    }),

    /**
     * @esbEndpoint GET /persons/v2/:personGuid/parentGuardianInvitation
     */
    getParentGuardianInvitationDetails: builder.query<
      GetParentGuardianInvitationDetailsResponse,
      {
        personGuid: string;
        invitationId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.invitationId}`,
        method: 'GET',
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/parentGuardianInvitation/create
     */
    postParentGuardianInvitation: builder.mutation<
      void,
      {
        payload: PostParentGuardianInvitationRequest;
        personGuid: string;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/create`,
        method: 'POST',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint PUT /persons/v2/:personGuid/parentGuardianInvitation/:parentRequestId
     */
    putParentGuardianInvitation: builder.mutation<
      void,
      {
        payload: PutParentGuardianInvitationRequest;
        personGuid: string;
        parentRequestId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.parentRequestId}`,
        method: 'PUT',
        body: arg.payload,
      }),
    }),

    /**
     * @esbEndpoint DELETE /persons/v2/:personGuid/parentGuardianInvitation/:parentRequestId
     */
    deleteParentGuardianInvitation: builder.mutation<
      void,
      {
        personGuid: string;
        parentRequestId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.parentRequestId}`,
        method: 'DELETE',
      }),
    }),

    /**
     * @esbEndpoint POST /persons/v2/:personGuid/parentGuardianInvitation/:parentRequestId/accept
     */
    postParentGuardianInvitationAccept: builder.mutation<
      void,
      {
        payload: PostParentGuardianInvitationAcceptRequest;
        personGuid: string;
        parentRequestId: number;
      }
    >({
      query: arg => ({
        url: `/persons/v2/${arg.personGuid}/parentGuardianInvitation/${arg.parentRequestId}/accept`,
        method: 'POST',
        body: arg.payload,
      }),
    }),
  }),
});

export const {
  useLazyPostMeritBadgeBlueCardsQuery,
  usePostRelationshipSearchMutation,
  useGetRelationshipsQuery,
  usePostRelationshipMutation,
  usePutRelationshipMutation,
  useDeleteRelationshipMutation,
  useGetParentGuardianInvitationQuery,
  useGetParentGuardianInvitationDetailsQuery,
  usePostParentGuardianInvitationMutation,
  usePutParentGuardianInvitationMutation,
  useDeleteParentGuardianInvitationMutation,
  usePostParentGuardianInvitationAcceptMutation,
} = youthProfileApi;
